import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { ChapterResponse, assetsChaptersBulkAddUpdateCreate } from '@goldcast/api/content';
import {
  computeChapterAdjustments,
  isChapterAffectingAction
} from '../clipUpdateDataUtilsWithSplits/chapterUpdateUtils';
import { UpdateClipAction } from '../clipUpdateDataUtilsWithSplits/types';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { loadChapters } from '@/context/TranscriptContext/TranscriptContextUtils';
import EventBus from '@/libs/eventBus/eventBus';
import { CustomEvents } from '@/libs/eventBus/constants';
import { getClipFPS } from '@/libs/clipUtils';
import { core } from '@/stores/core';
import { Clip } from '@/domains/asset';
import { showErrorToast } from '@/libs/toast/toast';

interface ChapterAdjustmentEvent {
  actionType: UpdateClipAction['actionType'];
  updateClipPayload: UpdateClipAction;
  oldClip: Clip;
  newClip: Clip;
}

export const useChapterAutoAdjust = (): void => {
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { clipData } = useClipsContext();
  const fps = useMemo(() => getClipFPS(clipData), [clipData]);
  const coreStore = useSyncExternalStore(core.subscribe, core.getSnapshot);
  const broadcastId = useMemo(() => coreStore.content?.id, [coreStore.content]);

  const handleChapterAdjustment = useCallback(
    async (event: ChapterAdjustmentEvent) => {
      if (!chapters || !broadcastId) {
        return;
      }
      if (!isChapterAffectingAction(event.actionType)) {
        return;
      }

      const result = computeChapterAdjustments({
        actionType: event.actionType,
        updateClipPayload: event.updateClipPayload,
        oldClip: event.oldClip,
        newClip: event.newClip,
        chapters
      });

      if (!result) {
        return;
      }

      await persistChapterChanges(result, broadcastId);
    },
    [chapters, fps, broadcastId]
  );

  useEffect(() => {
    const listener = EventBus.on(CustomEvents.ChapterAdjustmentNeeded, handleChapterAdjustment);
    return () => {
      EventBus.off(CustomEvents.ChapterAdjustmentNeeded, listener);
    };
  }, [handleChapterAdjustment]);
};

async function persistChapterChanges(result: ChapterResponse[], broadcastId: string): Promise<void> {
  try {
    // First, bulk update existing chapters
    await assetsChaptersBulkAddUpdateCreate({
      body: result,
      queryParams: { content_id: broadcastId }
    });

    // Reload from backend and update context
    const freshChapters = await loadChapters(broadcastId);
    EventBus.dispatch(CustomEvents.UpdateChapters, {
      chapterData: freshChapters?.chapters,
      chapterGenerationStatus: freshChapters?.chapter_generation_status,
      chaptersLastUpdatedAt: freshChapters?.latest_update_at
    });
  } catch (e) {
    window.logger?.error('Failed to persist chapter adjustments', e);
    showErrorToast('Failed to adjust chapters. Please try again.');
  }
}
